import { useState, useEffect } from "react";

/**
 * Reusable per-screen period override bar.
 *
 * Defaults to the global period (passed via `globalPeriod` prop) and lets the
 * user override locally for this screen only via calendar inputs + presets.
 * When `userOverride` is active, parent gets `{ from, to }` via `onChange`;
 * when reset to globálne, parent gets the globalPeriod values.
 *
 * Usage:
 *   const [localPeriod, setLocalPeriod] = useState(period);
 *   <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} />
 *   // Then use `localPeriod.from / localPeriod.to` in your queries.
 */
export default function PeriodOverrideBar({ globalPeriod, onChange, label = "Obdobie prehľadu", testIdPrefix = "period-override" }) {
  const [from, setFrom] = useState(globalPeriod?.from || "");
  const [to, setTo] = useState(globalPeriod?.to || "");
  const [userOverride, setUserOverride] = useState(false);

  // Sync from global when user hasn't overridden
  useEffect(() => {
    if (!userOverride) {
      const gFrom = globalPeriod?.from || "";
      const gTo = globalPeriod?.to || "";
      setFrom(gFrom);
      setTo(gTo);
      if (onChange) onChange({ from: gFrom, to: gTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalPeriod?.from, globalPeriod?.to]);

  const apply = (newFrom, newTo, override = true) => {
    setFrom(newFrom);
    setTo(newTo);
    setUserOverride(override);
    if (onChange) onChange({ from: newFrom, to: newTo });
  };

  const presetRange = (days) => {
    const t = new Date();
    const start = new Date(t);
    start.setDate(start.getDate() - (days - 1));
    apply(start.toISOString().slice(0, 10), t.toISOString().slice(0, 10), true);
  };

  const resetGlobal = () => {
    apply(globalPeriod?.from || "", globalPeriod?.to || "", false);
  };

  return (
    <div className="bg-[var(--bg-page)] rounded p-3 mb-3 border border-[var(--border-default)]">
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="text-xs">
          <p className="label-eyebrow mb-1">{label}</p>
          <p className="text-[10px] text-[var(--text-muted)]">
            {userOverride ? (
              <span className="text-[var(--brand)] font-semibold">⚙ Aktívny lokálny override</span>
            ) : (
              <>Default = globálne ({globalPeriod?.from || "—"} → {globalPeriod?.to || "—"})</>
            )}
          </p>
        </div>
        <div>
          <input
            type="date"
            value={from}
            onChange={(e) => apply(e.target.value, to, true)}
            className="tabular text-xs"
            data-testid={`${testIdPrefix}-from`}
          />
        </div>
        <span className="text-xs text-[var(--text-muted)] pb-1">→</span>
        <div>
          <input
            type="date"
            value={to}
            onChange={(e) => apply(from, e.target.value, true)}
            className="tabular text-xs"
            data-testid={`${testIdPrefix}-to`}
          />
        </div>
        <div className="flex flex-wrap gap-1 ml-auto">
          {[
            { id: "7d", label: "7d", days: 7 },
            { id: "30d", label: "30d", days: 30 },
            { id: "90d", label: "Kvartál", days: 90 },
            { id: "180d", label: "Polrok", days: 180 },
            { id: "365d", label: "Rok", days: 365 },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => presetRange(p.days)}
              className="text-[11px] px-2 py-1 rounded-full border border-[var(--border-default)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand)] hover:text-[var(--text-primary)]"
              data-testid={`${testIdPrefix}-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          {userOverride && (
            <button
              onClick={resetGlobal}
              className="text-[11px] px-2 py-1 rounded-full border border-[var(--brand)] bg-[var(--brand)] text-white"
              data-testid={`${testIdPrefix}-reset`}
              title="Návrat na globálne obdobie"
            >
              ↺ Globálne
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
